import React from 'react';
import shimbun1 from '../assets/shimbun1.png';
import shimbun2 from '../assets/shimbun2.png';
import shimbun3 from '../assets/shimbun3.png';
import './CSS/Shimbun.css';

function Articles() {
  return (
    <main className="morihei-page">
      {/* MASTHEAD */}
      <header className="masthead">
        <div className="masthead-top">
          <span className="masthead-meta">Est. B.E. 2569</span>
          <span className="masthead-meta">
            Published by the Aikido Association Thailand
          </span>
        </div>

        <div className="masthead-title-row">
          <div className="masthead-rule"></div>
          <h1 className="masthead-title">Thai Aikikai Shimbun</h1>
          <div className="masthead-rule"></div>
        </div>

        <div className="masthead-sub-row">
          <span className="masthead-volume">Volume. I</span>
          <span className="masthead-divider">✦</span>
          <span className="masthead-kanji">泰合気会新聞</span>
          <span className="masthead-divider">✦</span>
          <span className="masthead-volume">Inaugural Edition</span>
        </div>

        <div className="masthead-bottom-rule"></div>
      </header>

      {/* HERO */}
      <section className="hero">
        <div className="hero-left">
          <div className="label">
            Japanese Martial Arts · Aikido · Philosophy
          </div>

          <h1 className="hero-title">
            Morihei Ueshiba
            <br />
            "O'Sensei"
          </h1>

          <div className="hero-subtitle">植芝 盛平 · 1883–1969</div>
          <div className="divider-line"></div>

          <p className="hero-desc">
            The man who transformed the very philosophy of martial arts — from
            the art of conquest to the art of harmony and peace. Father of
            Aikido, the martial way of love.
          </p>
        </div>

        <div className="hero-right">
          <img src={shimbun1} alt="Morihei Ueshiba, Founder of Aikido" />
        </div>
      </section>

      {/* CONTENT */}
      <div className="container">
        <div className="pull-quote">
          <span className="kanji">合</span>
          "Aikido is not an art to fight with enemies and defeat them.
          <br />
          It is a way to reconcile the world and make human beings one family."
        </div>

        {/* CHAPTER ONE */}
        <div className="section">
          <div className="section-number">CHAPTER ONE</div>

          <h2 className="section-title">
            The Foundations of a Warrior
            <br />
            <span>From Jutsu to Do</span>
          </h2>

          <div className="img-block float-right">
            <img src={shimbun1} alt="O'Sensei Morihei Ueshiba" />
            <div className="img-caption">
              Morihei Ueshiba in formal portrait — the man who turned the power
              of violence into the art of peace.
            </div>
          </div>

          <p>
            Morihei Ueshiba was born on December 14, 1883, in Tanabe, Wakayama
            Prefecture, Japan. As a child he was frail and frequently bullied,
            prompting his father to encourage him to take up sumo wrestling and
            swimming to build his body. But the fire that burned within him
            could not be contained by ordinary exercise. He was driven by
            something deeper — a hunger to understand the true nature of
            strength.
          </p>

          <p>
            His formal martial training began in earnest during his early
            twenties. He studied <strong>Tenjin Shinyo-ryu Jujutsu</strong>, an
            ancient system renowned for its intricate joint locks and throws,
            and <strong>Goto-ha Yagyu-ryu Kenjutsu</strong>, a lineage of
            swordsmanship tracing directly to the samurai era. His dedication
            was absolute; he would train through pain, illness, and exhaustion,
            earning the admiration of his instructors and fellow students alike.
          </p>

          <p>
            The most pivotal encounter of his martial life came in 1915, when
            he met <strong>Sokaku Takeda</strong> in Hokkaido — the last great
            warrior of the Meiji era and master of{' '}
            <strong>Daito-ryu Aiki-jujutsu</strong>. Takeda's art was
            devastating: a refined system of joint manipulation, throws, and
            internal energy control passed down through the Aizu clan. Ueshiba
            became Takeda's most devoted student, training obsessively until he
            had mastered the full curriculum. He later received a certificate of
            mastery — a rare honor from the famously secretive Takeda.
          </p>

          <p>
            Yet for all its brilliance, Daito-ryu remained a warrior's art —
            designed to cripple and kill on the battlefield. Ueshiba had
            mastered the outer form perfectly, but something in his soul
            resisted. The seed of transformation had been planted. It was only
            waiting for the right moment to bloom.
          </p>
        </div>

        {/* CHAPTER TWO */}
        <div className="section clearfix">
          <div className="section-number">CHAPTER TWO</div>

          <h2 className="section-title">
            The Great Spiritual Awakening
            <br />
            <span>The Influence of Omoto-kyo</span>
          </h2>

          <p>
            The greatest transformation in O'Sensei's life did not come from
            the training hall. It did not come from a teacher, a tournament, or
            a battle. It rose from the depths of his own spirit — and it would
            change the course of martial arts history forever.
          </p>

          <p>
            In 1919, while traveling home to visit his gravely ill father,
            Ueshiba stopped in the town of Ayabe and encountered{' '}
            <strong>Onisaburo Deguchi</strong>, the charismatic spiritual
            leader of the <strong>Omoto-kyo</strong> religion. Deguchi was a
            visionary who taught that true power resided not in physical
            strength, but in love, harmony, and unity with the universe. He
            spoke of building a kingdom of peace on Earth — a divine
            civilization rooted in spiritual awakening. Ueshiba was transfixed.
            He decided to remain in Ayabe for several years, training his body
            each morning and his spirit each evening through prayer and
            meditation.
          </p>

          <p>
            During this period his reputation as a martial artist reached
            legendary heights. Challengers arrived from across Japan — seasoned
            fighters, military officers, swordsmen of the highest rank — and
            each one left humbled. Yet Ueshiba found no satisfaction in these
            victories. To defeat another man, he felt, was not an achievement
            worth celebrating. A deeper question was forming inside him:{' '}
            <em>What is martial art truly for?</em>
          </p>

          <p>
            The answer came in the spring of <strong>1925</strong>, in an event
            that would define the rest of his life. A naval officer challenged
            him to a duel with a wooden sword. Ueshiba, unarmed, evaded every
            strike with uncanny ease — not by speed alone, but by something
            else, something he could not yet fully name. Afterward, he walked
            out into his garden and felt the earth tremble beneath him. A
            column of golden light seemed to rise from the ground. He later
            described the experience in his own words:{' '}
            <em>
              "I felt the universe suddenly quake, and that a golden spirit
              sprang up from the ground, veiled my body, and changed my body
              into one of gold. At the same time my mind and body became light.
              I was able to understand the whispering of the birds, and was
              clearly aware of the mind of God."
            </em>
          </p>

          <p>
            This moment of <strong>enlightenment (Satori)</strong> revealed to
            him that the true purpose of martial training was not conquest — it
            was self-mastery. From that day forward, he began reshaping
            everything he knew into what would become <strong>Aikido</strong>:
            the Way of Harmonizing Energy — a martial art born not from the
            desire to dominate, but from the aspiration to protect all living
            things.
          </p>
        </div>
      </div>

      {/* FULL IMAGE */}
      <div className="container">
        <div className="full-image">
          <img src={shimbun2} alt="Aikido throw technique" />
          <div className="overlay-text">
            <p>
              An Aikido throw — not a clash of forces, but a seamless
              redirection of energy. The nage (thrower) guides; the uke
              (receiver) follows the natural arc of motion.
            </p>
          </div>
        </div>
      </div>

      <div className="container">
        {/* CHAPTER THREE */}
        <div className="section" style={{ marginTop: '60px' }}>
          <div className="section-number">CHAPTER THREE</div>

          <h2 className="section-title">
            The Essence of Aikido
            <br />
            <span>The Art of Harmony</span>
          </h2>

          <p>
            To truly understand Aikido, one must first set aside the familiar
            framework of winning and losing. Aikido was not created to defeat
            enemies — it was created to dissolve conflict without destroying
            anyone in the process. It is, as O'Sensei often said,{' '}
            <em>"the martial art of love."</em>
          </p>

          <p>
            At its heart lies the concept of <strong>Aiki</strong> — the
            blending of energies. When an attack comes, the Aikidoka does not
            brace against it or meet it with counter-force. Instead, they step
            in (Irimi) or pivot around (Tenkan), merging with the attacker's
            momentum, and guide that energy to a natural conclusion — a throw,
            a lock, a pin — that neutralizes the threat without requiring
            superior strength. This is why Aikido can be practiced effectively
            by people of all ages, sizes, and physical abilities. The principle
            scales with intent, not muscle.
          </p>

          <p>
            O'Sensei encapsulated this philosophy in the phrase{' '}
            <strong>"Masakatsu Agatsu"</strong> — "True victory is
            self-victory." The enemy is never truly the person in front of you.
            The real adversary is the aggression, the ego, and the fear within
            yourself. When those are overcome, harmony becomes possible with
            anyone.
          </p>

          <div className="principles">
            <div className="principle-card" data-kanji="合気">
              <h4>Aiki — Blending of Energy</h4>
              <p>
                Rather than opposing force with force, the Aikidoka merges with
                the attacker's movement — entering or pivoting to align with
                their momentum, then redirecting it. This neutralizes aggression
                while requiring minimal physical strength, making the technique
                effective regardless of size or age.
              </p>
            </div>

            <div className="principle-card" data-kanji="愛">
              <h4>Non-Violence — Protection for All</h4>
              <p>
                Every Aikido technique aims to control rather than injure.
                Joint locks and throws are designed to restrain, not maim.
                O'Sensei believed that even an attacker deserved protection —
                the goal is always to end conflict with the least possible harm
                to everyone involved.
              </p>
            </div>

            <div className="principle-card" data-kanji="道">
              <h4>Do — A Way of Life</h4>
              <p>
                The suffix "do" (道) means "way" or "path" — the same character
                found in Judo, Kendo, and Chado (Tea Ceremony). Aikido is a
                lifelong discipline of self-cultivation. Regular practice in
                the dojo trains the practitioner to remain calm under pressure,
                focused in chaos, and compassionate in conflict.
              </p>
            </div>

            <div className="principle-card" data-kanji="和">
              <h4>Ai — Love and Harmony</h4>
              <p>
                The Japanese character "Ai (合)" means "harmony," but it shares
                its pronunciation with "Ai (愛)" meaning "love." O'Sensei
                intended this double meaning — for he believed that true
                harmony can only arise when the heart is filled with love, not
                fear or hatred. This linguistic resonance is the philosophical
                soul of Aikido.
              </p>
            </div>
          </div>
        </div>

        {/* CHAPTER FOUR */}
        <div className="section clearfix">
          <div className="section-number">CHAPTER FOUR</div>

          <h2 className="section-title">
            Legacy and the Sacred Ground
            <br />
            <span>From Iwama to the World</span>
          </h2>

          <div className="img-block float-left">
            <img src={shimbun3} alt="Aiki Shrine, Iwama" />
            <div className="img-caption">
              The Aiki Shrine (Aiki Jinja) at Iwama — a spiritual center for
              Aikidoka from around the world, built by O'Sensei's own hands.
            </div>
          </div>

          <p>
            In the years following World War II, Morihei Ueshiba withdrew from
            the public world of martial arts demonstrations and settled in the
            quiet farming village of <strong>Iwama</strong> in Ibaraki
            Prefecture. There, among cedar forests and rice fields, he built the{' '}
            <strong>Tanrenkan dojo</strong> and the{' '}
            <strong>Aiki Shrine (Aiki Jinja)</strong> largely with his own
            hands. His daily rhythm was simple and profound: rise before dawn,
            pray at the shrine, work the fields, train, and teach.
          </p>

          <p>
            The Aiki Shrine was more than a religious monument. It was
            O'Sensei's declaration to the world that Aikido was a{' '}
            <em>budo of love</em> — a martial path inseparable from the
            spiritual aspiration to unite humanity with nature and with the
            divine. Today the shrine remains a place of pilgrimage for Aikidoka
            from every corner of the globe, who come to train on the same ground
            where the founder refined and perfected his art.
          </p>

          <p>
            After O'Sensei's passing, his son{' '}
            <strong>Kisshomaru Ueshiba</strong> assumed leadership of the{' '}
            <strong>Aikikai Foundation</strong> in Tokyo and systematically
            spread Aikido internationally through a global network of affiliated
            dojos. Meanwhile, O'Sensei's greatest students carried his teachings
            forward in their own distinctive ways:{' '}
            <strong>Koichi Tohei</strong> emphasized Ki development and founded
            Ki-Aikido; <strong>Morihiro Saito</strong> preserved the classical
            Iwama-style techniques with extraordinary fidelity; and{' '}
            <strong>Gozo Shioda</strong> founded Yoshinkan Aikido, renowned for
            its precise and powerful expression of the art.
          </p>

          <p>
            Today, <strong>Aikido</strong> is practiced in over 160 countries by
            millions of people — from police officers and military personnel who
            value its practical restraint techniques, to elderly practitioners
            who find in it a gentle system for health and balance, to
            philosophers and artists who are drawn to its ethical and aesthetic
            dimensions. The art has proven itself remarkably adaptable, speaking
            equally to those who seek physical mastery, mental discipline, or
            spiritual depth.
          </p>

          <p>
            Morihei Ueshiba passed away on April 26, 1969, at the age of 85. In
            his final years, even as his body weakened, his technique reportedly
            became more effortless and transcendent than ever — as if the art
            had finally shed its last traces of struggle. His parting words to
            his students were characteristically simple:{' '}
            <em>
              "Aikido is for the entire world. Train not for selfish reasons,
              but for all people everywhere."
            </em>{' '}
            That instruction remains the living heart of the art he left
            behind.
          </p>
        </div>

        {/* STATS */}
        <div className="stats-row">
          <div className="stat">
            <span className="stat-number">160+</span>
            <div className="stat-label">
              Countries
              <br />
              Worldwide
            </div>
          </div>

          <div className="stat">
            <span className="stat-number">1925</span>
            <div className="stat-label">
              Year of
              <br />
              Satori
            </div>
          </div>

          <div className="stat">
            <span className="stat-number">85</span>
            <div className="stat-label">
              Years of Life
              <br />
              1883–1969
            </div>
          </div>

          <div className="stat">
            <span className="stat-number">∞</span>
            <div className="stat-label">
              Spiritual Legacy
              <br />
              Endures
            </div>
          </div>
        </div>

        {/* END */}
        <div className="article-end">
          <span className="ornament">合気道</span>
          <p>The Way of Harmonizing Energy</p>
        </div>
      </div>
    </main>
  );
}

export default Articles;
